import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { ToggleSwitch } from '@/shared/components/custom/toggle-switch/ToggleSwitch';
import { useCreateTimeOffType } from '../../hooks/useCreateTimeOffType';
import { useUpdateTimeOffType } from '../../hooks/useUpdateTimeOffType';
import {
  createTimeOffTypeSchema,
  type CreateTimeOffTypeFormInput,
  type CreateTimeOffTypeFormValues,
} from '../../schemas';
import type { TimeOffTypeDto } from '../../types';

interface TypeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingType?: TimeOffTypeDto | null;
}

const EMPTY_VALUES: CreateTimeOffTypeFormInput = {
  name: '',
  code: '',
  consumesVacationBalance: false,
  requiresApproval: true,
  allowsRetroactive: false,
  allowsHourly: false,
  requiresAttachment: false,
  order: 0,
};

export function TypeFormDialog({ open, onOpenChange, editingType }: TypeFormDialogProps) {
  const { t } = useTranslation('timeOff');
  const isSystemType = !!editingType?.isSystem;
  const createType = useCreateTimeOffType();
  const updateType = useUpdateTimeOffType();

  const { control, handleSubmit, reset } = useForm<
    CreateTimeOffTypeFormInput,
    unknown,
    CreateTimeOffTypeFormValues
  >({
    resolver: zodResolver(createTimeOffTypeSchema(t)),
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    if (open) {
      reset(
        editingType
          ? {
              name: editingType.name ?? '',
              code: editingType.code ?? '',
              consumesVacationBalance: editingType.consumesVacationBalance,
              requiresApproval: editingType.requiresApproval,
              allowsRetroactive: editingType.allowsRetroactive,
              allowsHourly: editingType.allowsHourly,
              requiresAttachment: editingType.requiresAttachment,
              order: editingType.order,
            }
          : EMPTY_VALUES,
      );
    }
  }, [open, editingType, reset]);

  function onSubmit(values: CreateTimeOffTypeFormValues) {
    const { code: _code, ...updatePayload } = values;
    if (editingType?.id) {
      updateType.mutate(
        { typeId: editingType.id, payload: updatePayload },
        { onSuccess: () => onOpenChange(false) },
      );
    } else {
      createType.mutate(values, { onSuccess: () => onOpenChange(false) });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="time-off-settings-type-dialog">
        <DialogHeader>
          <DialogTitle>
            {editingType ? t('settings.types.editTitle') : t('settings.types.createTitle')}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-3"
          data-testid="time-off-settings-type-form"
        >
          <FormFieldInput
            control={control}
            name="name"
            label={t('settings.types.columns.name')}
            data-testid="time-off-settings-type-name"
          />
          {!editingType && (
            <FormFieldInput
              control={control}
              name="code"
              label={t('settings.types.columns.code')}
              data-testid="time-off-settings-type-code"
            />
          )}
          <FormFieldInput
            control={control}
            name="order"
            type="number"
            label={t('settings.types.columns.order')}
            data-testid="time-off-settings-type-order"
          />
          <Controller
            name="consumesVacationBalance"
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                id="type-consumes-balance"
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={isSystemType}
                label={t('settings.types.consumesBalance')}
                data-testid="time-off-settings-type-consumes-balance"
              />
            )}
          />
          <Controller
            name="allowsHourly"
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                id="type-allows-hourly"
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={isSystemType}
                label={t('settings.types.allowsHourly')}
                data-testid="time-off-settings-type-allows-hourly"
              />
            )}
          />
          <Controller
            name="requiresApproval"
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                id="type-requires-approval"
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={isSystemType}
                label={t('settings.types.requiresApproval')}
                data-testid="time-off-settings-type-requires-approval"
              />
            )}
          />
          <Controller
            name="requiresAttachment"
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                id="type-requires-attachment"
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={isSystemType}
                label={t('settings.types.requiresAttachment')}
                data-testid="time-off-settings-type-requires-attachment"
              />
            )}
          />
          <Controller
            name="allowsRetroactive"
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                id="type-allows-retroactive"
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={isSystemType}
                label={t('settings.types.allowsRetroactive')}
                data-testid="time-off-settings-type-allows-retroactive"
              />
            )}
          />
          {isSystemType && (
            <p className="text-xs text-muted-foreground">{t('settings.types.systemLocked')}</p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('settings.types.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={createType.isPending || updateType.isPending}
              data-testid="time-off-settings-type-submit"
            >
              {editingType ? t('settings.types.save') : t('settings.types.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
